import { Flex, Form, Table, TableProps, Typography } from "antd";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import { FORM_VALIDATION_DEBOUNCE } from "constants/form";
import { CONTRACT_CURRENCY_ENUM, NOTIFICATION_TYPE_ENUM } from "interfaces";
import { observer } from "mobx-react-lite";
import { useNotificationContext } from "providers";
import { useState } from "react";
import { getBidBalance } from "services";
import { getWalletTokens, TokenDepositWithId } from "services/walletTokens";
import { useStores } from "stores";
import { CustomButton, DefaultCheckbox, DefaultInput, DefaultSelect } from "ui";
import {
  _isRequired,
  _isValidNetworkAddress,
  getFirstOption,
  getTokensAsOptions,
  parseErrorMessage,
  renderSelectOptions,
  trimTrailingZeros,
} from "utils";
import { getColumns } from "./getColumns";

import styles from "./returnButton.module.css";
import { ConfirmReturnTokensModal } from "./ConfirmReturnTokensModal";

enum CHECK_BALANCE_STEP {
  CHECK = "CHECK",
  RESULT = "RESULT",
  CONFIRM = "CONFIRM",
}

export const ReturnTokensModal = observer(() => {
  const { actionModals, bidCard } = useStores();
  const { openNotification } = useNotificationContext();

  const [loading, setLoading] = useState(false);
  const [checkBalanceStep, setCheckBalanceStep] = useState(CHECK_BALANCE_STEP.CHECK);
  const [isCustomToken, setIsCustomToken] = useState(false);
  const [nativeBalance, setNativeBalance] = useState<number | null>(null);
  const [balance, setBalance] = useState<number | null>(null);
  const [depositList, setDepositList] = useState<TokenDepositWithId[]>([]);
  const [selectedWallet, setSelectedWallet] = useState<TokenDepositWithId | null>(null);

  const [form] = Form.useForm();

  const { network } = bidCard.bid;

  const currency = isCustomToken ? null : form.getFieldValue("token");
  const tokenOptions = Object.fromEntries(
    Object.entries(getTokensAsOptions(network || "ETH")).filter(([key]) => key !== bidCard.bid.currency)
  );

  const handleCloseModal = () => {
    actionModals.closeAll();
    setCheckBalanceStep(CHECK_BALANCE_STEP.CHECK);
    setBalance(null);
    setDepositList([]);
    setSelectedWallet(null);
    setIsCustomToken(false);
    form.resetFields();
  };

  const getSubmitButtonTooltip = (): string | undefined => {
    if (checkBalanceStep === CHECK_BALANCE_STEP.CHECK || loading) {
      return undefined;
    }
    if (balance === 0) {
      return "Нет средств для возврата";
    }
    if (nativeBalance === 0) {
      return `На кошельке должна быть ${network} для оплаты транзакции возврата`;
    }
    if (!selectedWallet) {
      return "Выберите адрес для возврата";
    }
    return "Средства будут возвращены на адрес отправителя";
  };

  const getSubmitButtonText = (): string => {
    if (checkBalanceStep === CHECK_BALANCE_STEP.CHECK) {
      return "Проверить баланс";
    }
    return "Вернуть токены";
  };

  const handleCheckBalance = async () => {
    if (!bidCard.bid.transitWallet) return;
    setLoading(true);

    const values = form.getFieldsValue();

    const dataToSend = {
      tokenAddress: isCustomToken ? values.tokenAddress : "",
      token: isCustomToken ? "" : values.token,
    };

    await getWalletTokens(bidCard.bid.transitWallet, dataToSend)
      .then((res) => {
        const formattedDeposits = res.deposits.map((deposit, index) => ({
          ...deposit,
          id: String(index),
        }));

        setDepositList(formattedDeposits);
        setBalance(res.balance);
        setCheckBalanceStep(CHECK_BALANCE_STEP.RESULT);

        if (formattedDeposits.length === 1) {
          setSelectedWallet(formattedDeposits[0]);
        }

        if (res.balance > 0) {
          setTimeout(() => {
            handleUpdateBalance();
          }, 500);
        } else {
          setLoading(false);
        }
      })
      .catch((e) => {
        openNotification({
          description: parseErrorMessage(e),
          type: NOTIFICATION_TYPE_ENUM.ERROR,
        });
        setLoading(false);
      });
  };

  const handleUpdateBalance = async () => {
    await getBidBalance({ id: bidCard.bid.id })
      .then(async () => {
        await bidCard.updateCardById(bidCard.bid.id);
        setNativeBalance(bidCard.bid.balance ? +bidCard.bid.balance.native : 0);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleChangeCustomToken = (e: CheckboxChangeEvent) => {
    const isCustom = e.target.checked;
    setIsCustomToken(isCustom);

    if (isCustom) {
      form.validateFields(["token"]);
    }
  };

  const rowSelection: TableProps<TokenDepositWithId>["rowSelection"] = {
    selectedRowKeys: selectedWallet?.id ? [selectedWallet.id] : [],
    onChange: (selectedRowKeys: React.Key[], selectedRows: TokenDepositWithId[]) => {
      setSelectedWallet(selectedRows[0]);
    },
    type: "radio",
  };

  const confirmRefund = () => {
    setCheckBalanceStep(CHECK_BALANCE_STEP.CONFIRM);
    actionModals.closeAll();
    const values = form.getFieldsValue();
    actionModals.open(
      () => (
        <ConfirmReturnTokensModal
          balance={balance || 0}
          wallet={selectedWallet?.from || ""}
          dataToSend={{
            tokenAddress: isCustomToken ? values.tokenAddress : null,
            token: isCustomToken ? null : values.token,
            recipient: selectedWallet?.from || "",
          }}
        />
      ),
      {
        width: "700px",
        noValidation: true,
        title: "Подтвердите действие",
        isOverlayClose: false,
        onApply: () => {
          return Promise.resolve();
        },
        onClose: () => handleCloseModal,
      }
    );
  };

  return (
    <>
      <Flex vertical>
        <Form
          form={form}
          name="returnTokens"
          initialValues={{
            currency: getFirstOption(CONTRACT_CURRENCY_ENUM),
            token: Object.keys(tokenOptions)[0] || "",
          }}
          onFinish={() => {
            if (checkBalanceStep === CHECK_BALANCE_STEP.CHECK) {
              handleCheckBalance();
            }
            if (checkBalanceStep === CHECK_BALANCE_STEP.RESULT) {
              confirmRefund();
            }
          }}
        >
          <Flex vertical hidden={checkBalanceStep === CHECK_BALANCE_STEP.RESULT}>
            <Form.Item
              name="token"
              rules={[
                {
                  required: !isCustomToken,
                  message: "Токен обязателен для ввода",
                },
              ]}
              validateDebounce={FORM_VALIDATION_DEBOUNCE}
            >
              <DefaultSelect
                placeholder="Токен"
                disabled={isCustomToken || checkBalanceStep === CHECK_BALANCE_STEP.RESULT}
                size="large"
              >
                {renderSelectOptions(tokenOptions)}
              </DefaultSelect>
            </Form.Item>
            <Form.Item>
              <DefaultCheckbox
                onChange={handleChangeCustomToken}
                defaultChecked={isCustomToken}
                disabled={checkBalanceStep === CHECK_BALANCE_STEP.RESULT}
              >
                Токена нет в списке
              </DefaultCheckbox>
            </Form.Item>
            {isCustomToken && (
              <Form.Item
                name="tokenAddress"
                rules={[_isRequired("Введите адрес смарт-контракта"), _isValidNetworkAddress(network)]}
                validateDebounce={FORM_VALIDATION_DEBOUNCE}
              >
                <DefaultInput
                  placeholder="Введите адрес смарт-контракта токена"
                  disabled={checkBalanceStep === CHECK_BALANCE_STEP.RESULT}
                />
              </Form.Item>
            )}
          </Flex>

          {checkBalanceStep === CHECK_BALANCE_STEP.RESULT && (
            <Flex vertical gap="middle">
              {depositList.length > 0 && (
                <Form.Item name="wallet" className="full-width">
                  <Table<TokenDepositWithId>
                    size="small"
                    rowKey="id"
                    rowSelection={depositList.length > 1 ? rowSelection : undefined}
                    dataSource={depositList}
                    columns={getColumns(currency)}
                    pagination={false}
                  />
                </Form.Item>
              )}
              {balance !== null && (
                <Flex justify="space-between">
                  <Typography.Text className={styles.balance}>Текущий баланс по токену</Typography.Text>

                  <Typography.Text className={styles.balance}>
                    {`${trimTrailingZeros(balance)} ${currency || ""}`}
                  </Typography.Text>
                </Flex>
              )}
            </Flex>
          )}

          <Form.Item className="margin-top-md">
            <Flex gap="large">
              <CustomButton
                htmlType="submit"
                disabled={
                  loading ||
                  (checkBalanceStep === CHECK_BALANCE_STEP.RESULT &&
                    (balance === null || balance === 0 || nativeBalance === 0 || !selectedWallet))
                }
                loading={loading}
                tooltip={getSubmitButtonTooltip()}
              >
                {getSubmitButtonText()}
              </CustomButton>
              <CustomButton
                htmlType="button"
                disabled={loading}
                loading={loading}
                type="default"
                onClick={handleCloseModal}
              >
                Отмена
              </CustomButton>
            </Flex>
          </Form.Item>
        </Form>
      </Flex>
    </>
  );
});
